import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { moroccoStartOfToday } from "@majourneys/shared";
import { normalizeLocale } from "../lib/locale";
import { BEVEL_CHIP_ON, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";
export function buildWeekDays(lang: string): string[] {
  // narrow → single grapheme per locale (en "S", fr "D", ar "ح").
  // short → multi-char ("Sun", "dim.", "الأحد") which we'd have to cut by
  // grapheme; narrow sidesteps that and renders consistently in tight columns.
  const fmt = new Intl.DateTimeFormat(lang, { weekday: "narrow" });
  // Weeks start on Monday in every language: Morocco and Europe both count
  // from Monday (website test, 2026-11-01, the grid opened on Sunday).
  const refMonday = new Date(2024, 0, 8);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(refMonday);
    d.setDate(refMonday.getDate() + i);
    return fmt.format(d);
  });
}

/** Empty cells before the 1st of a month in a Monday-first grid. */
export function mondayFirstOffset(year: number, month: number): number {
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

export function diffDaysISO(a: string, b: string) {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

/* Selected days wear the soft-bevel navy face, like every chosen chip on the
   booking surface. */
/**
 * The navy a chosen day wears. Exported so the sheet's quick date grid and
 * time pills wear the same one — the two views are a toggle apart, and a day
 * that changes shade when you switch between them reads as a different day.
 */
export const CALENDAR_SELECTED_GRADIENT = BEVEL_NAVY_FACE;

export function CalendarGrid({
  selected,
  onSelect,
  isDateDisabled,
  maxDate,
  allowedDates,
  startAtSelected = false,
  startAt,
  slide = false,
}: {
  readonly selected: string | null;
  readonly onSelect: (iso: string) => void;
  /** Last bookable day (`YYYY-MM-DD`); later days are disabled like past ones. */
  readonly maxDate?: string;
  /** Extra per-day veto (a venue closed on that weekday). Past days are always disabled. */
  readonly isDateDisabled?: (iso: string) => boolean;
  /**
   * The only days that can be picked (`YYYY-MM-DD`), for a trip that runs on
   * fixed dates. Every other day is struck out like a past one, the grid
   * opens on the first of them, and it does not page past the last. Omitted
   * or empty: every day is a candidate, as before.
   */
  readonly allowedDates?: readonly string[];
  /**
   * Open on the selected day's month rather than this month. For a grid that
   * mounts fresh each time a popover opens, so a guest who picked a date next
   * month does not land a month early when they come back to change it.
   */
  readonly startAtSelected?: boolean;
  /**
   * YYYY-MM-DD. The month to open on when nothing is selected yet: a
   * check-out field opens on the check-in's month, not on this one.
   */
  readonly startAt?: string;
  /**
   * One navy circle slides to the day tapped instead of each day lighting up
   * (the motion kit, owner 2026-10-05). Off by default: the transport card
   * opts in.
   */
  readonly slide?: boolean;
}) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  // Latin digits in every locale, ar included (see CLAUDE.md, Web i18n).
  const numberFmt = useMemo(() => new Intl.NumberFormat(`${normalizeLocale(lang)}-u-nu-latn`), [lang]);
  const weekDays = useMemo(() => buildWeekDays(lang), [lang]);
  const today = useMemo(() => moroccoStartOfToday(), []);
  const allowed = useMemo(
    () => (allowedDates && allowedDates.length > 0 ? [...allowedDates].sort() : null),
    [allowedDates],
  );
  const allowedSet = useMemo(() => (allowed ? new Set(allowed) : null), [allowed]);
  // A fixed-date trip pages no further than its last date.
  const lastDate = allowed ? (maxDate && maxDate < allowed[allowed.length - 1]! ? maxDate : allowed[allowed.length - 1]!) : maxDate;
  const [view, setView] = useState(() => {
    // A fixed-date trip opens on the month of its first date, not this one.
    const from = (startAtSelected && selected) || startAt || allowed?.[0] || null;
    const m = from ? /^(\d{4})-(\d{2})-\d{2}$/.exec(from) : null;
    return m
      ? { year: Number(m[1]), month: Number(m[2]) - 1 }
      : { year: today.getFullYear(), month: today.getMonth() };
  });

  const firstDay = mondayFirstOffset(view.year, view.month);
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(lang, {
    month: "long",
    year: "numeric",
  });

  // No paging into a month that holds no bookable day.
  const atLastMonth =
    !!lastDate && `${view.year}-${pad(view.month + 1)}` >= lastDate.slice(0, 7);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // No paging back before the current month: every day there is in the past.
  const atFirstMonth =
    view.year < today.getFullYear() ||
    (view.year === today.getFullYear() && view.month <= today.getMonth());

  function shift(delta: number) {
    if (delta < 0 && atFirstMonth) return;
    setView((v) => {
      const m = v.month + delta;
      if (m < 0) return { year: v.year - 1, month: 11 };
      if (m > 11) return { year: v.year + 1, month: 0 };
      return { year: v.year, month: m };
    });
  }

  /* The sliding circle: placed under the chosen day, without a slide the
     first time, after a month change, or for reduced motion. */
  const daysRef = useRef<HTMLDivElement>(null);
  const [dayHl, setDayHl] = useState<{ x: number; y: number; w: number; h: number; instant: boolean } | null>(null);
  const shownMonth = `${view.year}-${view.month}`;
  const lastMonth = useRef(shownMonth);
  useLayoutEffect(() => {
    const tile = slide && selected ? daysRef.current?.querySelector<HTMLElement>(`[data-iso="${selected}"]`) : null;
    const monthChanged = lastMonth.current !== shownMonth;
    lastMonth.current = shownMonth;
    if (!tile) {
      setDayHl(null);
      return;
    }
    const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    setDayHl((prev) => ({
      x: tile.offsetLeft,
      y: tile.offsetTop,
      w: tile.offsetWidth,
      h: tile.offsetHeight,
      instant: prev === null || monthChanged || Boolean(still),
    }));
  }, [slide, selected, shownMonth]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shift(-1)}
          disabled={atFirstMonth}
          aria-label={t("calendar.previous_month", { defaultValue: "Previous month" })}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#5a5d77] transition hover:bg-[rgba(4,0,51,0.05)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Icon name={isRtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} aria-hidden="true" />
        </button>
        <span className="font-sans text-[14px] font-bold text-[#110E39]">{monthLabel}</span>
        <button
          type="button"
          onClick={() => shift(1)}
          disabled={atLastMonth}
          aria-label={t("calendar.next_month", { defaultValue: "Next month" })}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#5a5d77] transition hover:bg-[rgba(4,0,51,0.05)] disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Icon name={isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1 text-center font-sans text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#5a5d77]">
        {weekDays.map((w, i) => (
          <div key={i}>{w}</div>
        ))}
      </div>
      <div ref={daysRef} className={`mt-1 grid grid-cols-7 gap-1${slide ? " relative" : ""}`}>
        {dayHl && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 rounded-full"
            style={{
              width: dayHl.w,
              height: dayHl.h,
              transform: `translate(${dayHl.x}px, ${dayHl.y}px)`,
              transition: dayHl.instant ? "none" : "transform 240ms cubic-bezier(0.23, 1, 0.32, 1)",
              background: CALENDAR_SELECTED_GRADIENT,
              // A chosen day is a chip: the bevel face, no drop shadow.
              boxShadow: BEVEL_CHIP_ON,
            }}
          />
        )}
        {cells.map((day, i) => {
          if (day === null) return <div key={`pad-${i}`} />;
          const iso = `${view.year}-${pad(view.month + 1)}-${pad(day)}`;
          const d = new Date(view.year, view.month, day);
          const isPast =
            d < today ||
            (!!lastDate && iso > lastDate) ||
            (allowedSet !== null && !allowedSet.has(iso)) ||
            Boolean(isDateDisabled?.(iso));
          const active = selected === iso;
          const onCircle = active && Boolean(dayHl);
          return (
            <button
              key={iso}
              type="button"
              data-iso={iso}
              aria-pressed={active}
              onClick={() => !isPast && onSelect(iso)}
              disabled={isPast}
              className={`h-9 rounded-full font-sans text-[13px] font-semibold outline-none transition focus-visible:shadow-[0_0_0_2px_#110E39]${slide ? " relative" : ""} ${
                active
                  ? "text-white"
                  : isPast
                    ? "text-[#c7c8d6] line-through"
                    : "text-[#110E39] hover:bg-[rgba(17,14,57,0.06)]"
              }`}
              style={
                active && !onCircle
                  ? {
                      background: CALENDAR_SELECTED_GRADIENT,
                      boxShadow: BEVEL_CHIP_ON,
                    }
                  : undefined
              }
            >
              {numberFmt.format(day)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The stay's nights: the site's soft gold, solid so overlaps never show. */
const RANGE_BAND = "#F8ECD9";

export function RangeCalendarGrid({
  checkIn,
  checkOut,
  minNights,
  unavailableDates,
  onChange,
}: {
  readonly checkIn: string | null;
  readonly checkOut: string | null;
  readonly minNights: number;
  /**
   * Check-in dates known to have nothing bookable, as YYYY-MM-DD.
   *
   * Omitted means "we do not know", and unknown must stay selectable. This is
   * the difference between a sparse property and a broken one: a guest on a
   * villa tried date after date and every single one answered "no rooms", with
   * nothing on screen to say which ones could ever have worked. Disabling the
   * known-empty dates ends that hunt. Disabling the UNKNOWN ones would invent
   * sold-out inventory, since suppliers routinely have no rates loaded months
   * out and the caller's map only reaches as far as it has been swept.
   *
   * A snapshot, not a promise: a date left open here can still fail at search.
   * The live check downstream is unchanged and remains the answer.
   */
  readonly unavailableDates?: readonly string[];
  readonly onChange: (next: { checkIn: string | null; checkOut: string | null }) => void;
}) {
  const { i18n, t: tx } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";
  const numberFmt = useMemo(() => new Intl.NumberFormat(`${normalizeLocale(lang)}-u-nu-latn`), [lang]);
  const weekDays = useMemo(() => buildWeekDays(lang), [lang]);
  const today = useMemo(() => moroccoStartOfToday(), []);
  // Opens on the chosen check-in's month, not today's: a guest who picked
  // 14 February and reopened the calendar landed back in September and had to
  // page forward five months to see their own dates (owner, 2026-09-26).
  const [view, setView] = useState(() => {
    const m = checkIn ? /^(\d{4})-(\d{2})-\d{2}$/.exec(checkIn) : null;
    const picked = m ? { year: Number(m[1]), month: Number(m[2]) - 1 } : null;
    const now = { year: today.getFullYear(), month: today.getMonth() };
    return picked && (picked.year > now.year || (picked.year === now.year && picked.month >= now.month))
      ? picked
      : now;
  });
  const [hover, setHover] = useState<string | null>(null);
  // A Set, not an array scan: the grid asks about ~42 cells on every render and
  // every month change, and the map can carry a full 90-date sweep.
  const unavailable = useMemo(
    () => new Set(unavailableDates ?? []),
    [unavailableDates],
  );

  const firstDay = mondayFirstOffset(view.year, view.month);
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(lang, {
    month: "long",
    year: "numeric",
  });

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // No paging back before the current month: every day there is in the past.
  const atFirstMonth =
    view.year < today.getFullYear() ||
    (view.year === today.getFullYear() && view.month <= today.getMonth());

  function shift(delta: number) {
    if (delta < 0 && atFirstMonth) return;
    setView((v) => {
      const m = v.month + delta;
      if (m < 0) return { year: v.year - 1, month: 11 };
      if (m > 11) return { year: v.year + 1, month: 0 };
      return { year: v.year, month: m };
    });
  }

  /**
   * Would clicking `iso` set the CHECK-IN rather than the check-out?
   *
   * True on an empty calendar, on a completed range (the next click starts a
   * new one), and on any date at or before the current check-in (which
   * restarts the range there). Mirrors handleClick's branches exactly — the
   * two must agree, or a date would be clickable and then do nothing.
   */
  function wouldSetCheckIn(iso: string): boolean {
    return !checkIn || Boolean(checkOut) || iso <= checkIn;
  }

  function isBlockedCheckIn(iso: string): boolean {
    return unavailable.has(iso) && wouldSetCheckIn(iso);
  }

  /**
   * The first night from `checkIn` onwards the property cannot sell.
   *
   * An unavailable date means a ONE night stay starting there did not sell —
   * the sweep probes 1, 2, 3, 5 and 7 nights and marks the date unbookable
   * only when every one of them came back empty (see DEFAULT_NIGHT_OPTIONS in
   * stays.availability.ts). So it is a statement about that NIGHT, not merely
   * about stays beginning on it, and any range covering it is unsellable.
   */
  const firstBlockedNight = useMemo(() => {
    if (!checkIn || unavailable.size === 0) return null;
    let earliest: string | null = null;
    for (const iso of unavailable) {
      if (iso < checkIn) continue;
      if (earliest === null || iso < earliest) earliest = iso;
    }
    return earliest;
  }, [checkIn, unavailable]);

  /**
   * Stops a guest picking a check-out on the far side of a sold-out night.
   *
   * This is what used to send them to "No rooms for these dates" from a
   * calendar that had offered every date involved: the check-in was genuinely
   * bookable and therefore not struck out, and nothing then objected to a
   * check-out days later with an unsellable night in between. The guest was
   * told the DATES were wrong when what was wrong was one night inside them.
   *
   * The blocked night itself stays selectable as a check-out: a guest leaving
   * that morning never sleeps it.
   */
  function isBlockedCheckOut(iso: string): boolean {
    if (wouldSetCheckIn(iso)) return false;
    return firstBlockedNight !== null && iso > firstBlockedNight;
  }

  function handleClick(iso: string) {
    if (isBlockedCheckIn(iso) || isBlockedCheckOut(iso)) return;
    if (!checkIn || (checkIn && checkOut)) {
      onChange({ checkIn: iso, checkOut: null });
      return;
    }
    if (iso <= checkIn) {
      onChange({ checkIn: iso, checkOut: null });
      return;
    }
    if (diffDaysISO(checkIn, iso) < minNights) return;
    onChange({ checkIn, checkOut: iso });
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shift(-1)}
          disabled={atFirstMonth}
          aria-label={tx("calendar.previous_month", { defaultValue: "Previous month" })}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#5a5d77] transition hover:bg-[rgba(4,0,51,0.05)] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Icon name={isRtl ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} aria-hidden="true" />
        </button>
        <span className="font-sans text-[14px] font-bold text-[#110E39]">{monthLabel}</span>
        <button
          type="button"
          onClick={() => shift(1)}
          aria-label={tx("calendar.next_month", { defaultValue: "Next month" })}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#5a5d77] transition hover:bg-[rgba(4,0,51,0.05)]"
        >
          <Icon name={isRtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-1 text-center font-sans text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#5a5d77]">
        {weekDays.map((w, i) => (
          <div key={i}>{w}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) return <div key={`pad-${i}`} />;
          const iso = `${view.year}-${pad(view.month + 1)}-${pad(day)}`;
          const d = new Date(view.year, view.month, day);
          const isPast = d < today;
          const isStart = checkIn === iso;
          const isEnd = checkOut === iso;
          const tooClose =
            checkIn && !checkOut && iso > checkIn && diffDaysISO(checkIn, iso) < minNights;
          const soldOut = !isPast && (isBlockedCheckIn(iso) || isBlockedCheckOut(iso));
          const rangeEnd = checkOut ?? (checkIn && hover && hover > checkIn ? hover : null);
          const inRange = Boolean(checkIn && rangeEnd && iso > checkIn && iso < rangeEnd);
          /**
           * THE BAND IS DRAWN BEHIND THE CELLS, NOT AS THE CELLS.
           *
           * The grid is `gap-1`, so colouring the cell itself left a 4px hole
           * between every day and a five-night stay read as five separate
           * highlights rather than one span. This layer reaches into the gap
           * on whichever side the range continues, and stops flush where it
           * ends, which is what makes it look like a single drawn band.
           *
           * Logical properties, not left/right: Arabic lays the week out
           * right to left and a physical inset would extend the wrong way.
           */
          const banded = inRange || ((isStart || isEnd) && Boolean(checkIn && rangeEnd));
          const col = i % 7;
          const continuesBefore = banded && !isStart && col !== 0;
          const continuesAfter =
            banded && !(checkOut ? isEnd : false) && col !== 6 && (inRange || isStart);
          return (
            <button
              key={iso}
              type="button"
              disabled={isPast || Boolean(tooClose) || soldOut}
              /**
               * Struck through, like a past date, rather than merely dimmed.
               * `tooClose` uses the same grey but no strike, because those
               * dates become available the moment the guest moves their
               * check-in; a sold-out date will not, and the two should not
               * read as the same kind of "not now".
               */
              aria-label={
                soldOut
                  ? `${numberFmt.format(day)}, ${tx("calendar.unavailable", {
                      defaultValue: "no availability",
                    })}`
                  : undefined
              }
              onClick={() => !isPast && !tooClose && handleClick(iso)}
              onMouseEnter={() => !isPast && setHover(iso)}
              onMouseLeave={() => setHover(null)}
              className={`relative h-9 font-sans text-[13px] font-semibold transition ${
                isStart || isEnd
                  ? "z-[1] grid place-items-center text-white"
                  : inRange
                    /**
                     * A 10% navy band, written as an explicit rgba rather than
                     * `bg-[#110E39] navy-depth/10`. That was the intent, but
                     * `navy-depth` is a plain custom class, so the `/10` did
                     * nothing: the day rendered at FULL navy with navy text on
                     * top, i.e. a solid dark rectangle with the number
                     * invisible inside it, between the two round endpoints.
                     */
                    ? "text-[#110E39]"
                    : isPast
                      ? "text-[#c7c8d6] line-through"
                      : soldOut
                        ? "rounded-full text-[#c7c8d6] line-through"
                        : tooClose
                          ? "rounded-full text-[#c7c8d6]"
                          : "rounded-full text-[#110E39] hover:bg-[rgba(4,0,51,0.05)]"
              }`}
            >
              {/* Behind the number, and behind the endpoint circle, which
                  keeps its own `z-[1]`. `key` is the band's shape: when a
                  guest drags the range the layer remounts and the growth
                  animation replays, instead of silently resizing. */}
              {inRange ? (
                <span
                  key={`${checkIn}-${rangeEnd}`}
                  aria-hidden="true"
                  className="mj-range-band absolute inset-y-0 z-0"
                  /* Opaque, not a see-through tint: each day's piece reaches
                     4px into the gap, and two translucent pieces overlapping
                     there drew a darker seam between every night (owner,
                     2026-09-26: "I don't like this style"). */
                  style={{
                    background: RANGE_BAND,
                    insetInlineStart: continuesBefore ? "-4px" : 0,
                    insetInlineEnd: continuesAfter ? "-4px" : 0,
                    borderStartStartRadius: continuesBefore ? 0 : 9999,
                    borderEndStartRadius: continuesBefore ? 0 : 9999,
                    borderStartEndRadius: continuesAfter ? 0 : 9999,
                    borderEndEndRadius: continuesAfter ? 0 : 9999,
                  }}
                />
              ) : null}
              {/* The endpoint's own half of the band, so the navy circle sits
                  on a continuous span rather than floating beside a gap. */}
              {(isStart || isEnd) && checkIn && rangeEnd && checkIn !== rangeEnd ? (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 z-0"
                  style={{
                    background: RANGE_BAND,
                    insetInlineStart: isStart ? "50%" : "-4px",
                    insetInlineEnd: isStart ? "-4px" : "50%",
                    display: (isStart && col === 6) || (isEnd && col === 0) ? "none" : undefined,
                  }}
                />
              ) : null}
              {isStart || isEnd ? (
                /* A true circle sitting on the band. The button is wider
                   than it is tall, so rounding the button itself drew an
                   oval; no drop shadow, which read as a heavy sticker. */
                <span
                  className="mj-day-pop relative z-[1] grid h-9 w-9 place-items-center rounded-full"
                  style={{ background: CALENDAR_SELECTED_GRADIENT }}
                >
                  {numberFmt.format(day)}
                </span>
              ) : (
                <span className="relative z-[1]">{numberFmt.format(day)}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
